import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Search } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@flexibilitypay/ui';
import { validateCuit } from '@flexibilitypay/shared';

import { useAccountsByCuitQuery } from '../hooks/use-accounts-by-cuit-query';
import { AccountSelector } from '../features/accounts';
import type { CuentaByCuit } from '../api/balance.api';

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T061
 * @see functional/Specs/specs_cycle-2.md HU-13-FE
 */
export function AccountsByCuitPage() {
  const { t } = useTranslation(['accounts']);
  const navigate = useNavigate();
  const [cuitInput, setCuitInput] = useState('');
  const [submittedCuit, setSubmittedCuit] = useState<string | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data, isFetching, isError } = useAccountsByCuitQuery(submittedCuit);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = cuitInput.replace(/\D/g, '');
    if (normalized.length !== 11 || !validateCuit(normalized)) {
      setValidationError(t('accounts:search.invalidCuit'));
      setSubmittedCuit(undefined);
      return;
    }
    setValidationError(null);
    setSubmittedCuit(normalized);
  }

  function handleSelect(cuenta: CuentaByCuit) {
    if (cuenta.idCuenta) {
      navigate(`/balance/${cuenta.idCuenta}`);
    }
  }

  const cuentas: CuentaByCuit[] = (data ?? []).flatMap((item) => item.cuentas ?? []);
  const hasSearched = !!submittedCuit && !isFetching && !isError;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">
          {t('accounts:page.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('accounts:page.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('accounts:search.title')}</CardTitle>
          <CardDescription>{t('accounts:search.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <div className="flex-1 space-y-2">
              <Label htmlFor="cuit-input">{t('accounts:search.inputLabel')}</Label>
              <Input
                id="cuit-input"
                name="cuit"
                inputMode="numeric"
                placeholder={t('accounts:search.inputPlaceholder')}
                value={cuitInput}
                onChange={(e) => setCuitInput(e.target.value)}
                aria-invalid={validationError ? true : undefined}
              />
              {validationError && (
                <p role="alert" className="text-xs text-destructive">
                  {validationError}
                </p>
              )}
            </div>
            <Button type="submit" disabled={isFetching}>
              <Search aria-hidden />
              {isFetching ? t('accounts:search.searching') : t('accounts:search.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {isError && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <AlertCircle className="size-4 mt-0.5 shrink-0" aria-hidden />
          <p className="font-medium">{t('accounts:search.error')}</p>
        </div>
      )}

      {hasSearched && <AccountSelector cuentas={cuentas} onSelect={handleSelect} />}
    </div>
  );
}
